import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { cloneGenericResearchQuerySchema } from "../../schemas";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { EditAuthTags } from "../AuthTags";
import { useToast } from "../../services/utils";

interface CloneResearchQueryDialogProps {
  cloneResearchQueryDialog: boolean;
  handleCloseCloneResearchQuery: any;
  cloneResearchQueryData: any;
  setCloneResearchQueryData: React.Dispatch<React.SetStateAction<any>>;
  onSaveCloneResearchQuery: any;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CloneResearchQueryDialog = ({
  cloneResearchQueryDialog,
  handleCloseCloneResearchQuery,
  cloneResearchQueryData,
  setCloneResearchQueryData,
  onSaveCloneResearchQuery,
  setIsLoading,
}: CloneResearchQueryDialogProps) => {
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = async (name: any, value: any) => {
    try {
      const partialReferenceData = { ...cloneResearchQueryData, [name]: value };
      await cloneGenericResearchQuerySchema.validateAt(
        name,
        partialReferenceData,
      );
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      await cloneGenericResearchQuerySchema.validate(cloneResearchQueryData, {
        abortEarly: false,
      });
      onSaveCloneResearchQuery();
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors(newErrors);
    }
  };

  const handleClose = (reason: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  };

  const handleCloseDialog = () => {
    setErrors({});
    handleCloseCloneResearchQuery();
  };

  const handleChangeCloneResearchQuery = (name: any, value: any) => {
    setCloneResearchQueryData({
      ...cloneResearchQueryData,
      [name]: value,
    });
    validateField(name, value);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={cloneResearchQueryDialog}
      onClose={(event, reason) => handleClose(reason)}
      className="modal-dialog-1"
    >
      <DialogTitle className="dialog-title" component="span">
        <span>Clone Research Query</span>
        <button className="close-icon" onClick={handleCloseDialog}></button>
      </DialogTitle>
      <DialogContent className="dialog-content">
        <Box>
          <Grid container rowSpacing={3} columnSpacing={3}>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <TextField
                type="text"
                name="name"
                value={cloneResearchQueryData?.name || ""}
                onChange={(e) =>
                  handleChangeCloneResearchQuery(e.target.name, e.target.value)
                }
                fullWidth
                variant="outlined"
                label={
                  <span>
                    Research Query Name
                    <span className="required-asterisk">*</span>
                  </span>
                }
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors?.name}
                helperText={errors?.name || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <TextField
                type="text"
                name="code"
                value={cloneResearchQueryData?.code || ""}
                onChange={(e) =>
                  handleChangeCloneResearchQuery(e.target.name, e.target.value)
                }
                fullWidth
                variant="outlined"
                label={
                  <span>
                    Research Query Code
                    <span className="required-asterisk">*</span>
                  </span>
                }
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors?.code}
                helperText={errors?.code || ""}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={4}
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <TextField
                type="text"
                name="description"
                value={cloneResearchQueryData?.description || ""}
                onChange={(e) =>
                  handleChangeCloneResearchQuery(e.target.name, e.target.value)
                }
                label={<span>Description</span>}
                className={`form-control-autocomplete`}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
              <EditAuthTags
                selectedTagIds={
                  cloneResearchQueryData?.generic_research_query_tags || []
                }
                onTagsChange={(selectedIds) => {
                  handleChangeCloneResearchQuery(
                    "generic_research_query_tags",
                    selectedIds,
                  );
                }}
                entityName="Research Query"
                showToast={showToast}
                errors={errors?.generic_research_query_tags}
                validateField={validateField}
                fieldName="generic_research_query_tags"
                label="Tag(s)"
                required={true}
                useActiveTagsOnly={true}
                setIsLoading={setIsLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions className="dialog-footer">
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={handleSubmit}
          className="btn-orange"
        >
          <SaveOutlinedIcon /> &nbsp; Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneResearchQueryDialog;
